<template>
  <v-dialog
    v-if="dialogAttachmentType !== null"
    v-model="modalState"
    width="800"
    content-class="attachments-modal"
    persistent
    @click:outside="clickOutside()"
  >
    <v-card
      class="pb-5"
      style="min-height: 675px"
    >
      <!-- add attachment header -->
      <v-card-title
        v-if="!isFileSelected"
        class="pb-5"
      >
        <!-- close -->
        <btn
          label="Close"
          :icon="icons.mdiClose"
          color="secondary"
          @click="closeModal"
        ></btn>
        <v-spacer></v-spacer>
        Attachments
        <v-spacer></v-spacer>

        <!-- select attachment -->
        <btn
          label="Select Attachment"
          :icon="$store.getters['baseData/bucketTypeFromValue'](dialogAttachmentType).icon"
          :disabled="signed && dialogAttachmentType === 'images'"
          @click="selectAttachmentClicked"
        ></btn>
        <input
          ref="uploader"
          type="file"
          class="d-none"
          accept="image/*, application/pdf"
          @change="onFileChanged"
        />
      </v-card-title>

      <!-- upload attachment header -->
      <v-form
        v-else
        ref="uploadForm"
        v-model="formValid"
        @submit.prevent="addFile"
      >
        <v-card-title class="pb-5">
          <!-- cancel -->
          <btn
            label="Cancel"
            :icon="icons.mdiClose"
            color="secondary"
            @click="cancelUpload(false)"
          ></btn>

          <v-spacer></v-spacer>

          <!-- name file -->
          <text-field
            v-model="fileDescription"
            label="Attachment Description"
            required
            maxlength="120"
            hint="The following characters are not allowed: / \ : * ? &quot; < > |"
            style="margin-bottom: -25px"
            @blur="normalizeFilename"
          ></text-field>

          <v-spacer></v-spacer>

          <img
            v-if="isImageType(attachment.attachment_mime_type)"
            :src="imagePreview"
            class="image-preview"
          >
          <div
            v-else
            class="image-preview mime-type"
          >
            {{ mimeType(attachment.attachment_mime_type) }}
          </div>

          <v-spacer></v-spacer>

          <!-- add file -->
          <btn
            label="Add File"
            :disabled="!formValid"
            :icon="icons.mdiFileImagePlus"
            :loading="isAddingFile"
            @click="addFile"
          ></btn>
        </v-card-title>
      </v-form>

      <!-- attachment type list -->
      <v-card-text>
        <v-btn-toggle
          v-model="dialogAttachmentTypeId"
          class="d-flex align-center justify-center"
        >
          <btn
            v-for="bucket in filteredAudioBucketType"
            :key="bucket.value"
            :disabled="isFileSelected"
            :label="bucket.title"
            :icon="bucket.icon"
            :value="bucket.value"
            :style="`width:${100 / filteredAudioBucketType.length}%`"
            :color="dialogAttachmentType === bucket.value ? 'success' : '#ddd'"
            @click="attachmentTypeClicked(bucket)"
          >
            <v-badge
              v-if="attachmentCount(bucket.value)"
              color="success"
              :content="attachmentCount(bucket.value)"
              inline
            >
              {{ bucket.title }}
            </v-badge>
          </btn>
        </v-btn-toggle>
      </v-card-text>
      <v-data-table
        :headers="headers"
        :items="imagesOnly ? imageOnlyAttachments : localAttachments"
        :class="isFileSelected && 'disable'"
        item-key="id"
        sort-by="created"
        :sort-desc="true"
        disable-pagination
        hide-default-footer
        @click:row="viewAttachment"
      >
        <!-- image -->
        <template #item.data="{ item }">
          <v-img
            v-if="isImageType(item.attachment_mime_type) && item.data !== 'placeholder'"
            :lazy-src="item.data"
            :src="item.data"
            max-width="250"
            max-height="150"
          ></v-img>
          <pdf
            v-else-if="isPDFType(item.attachment_mime_type) && item.data !== 'placeholder'"
            :src="item.data"
            :page="1"
            class="pdf-preview"
          ></pdf>
          <btn
            v-else-if="item.data === 'placeholder'"
            label="Click to view attachment"
            color="primary"
            offline-disable
          ></btn>
          <!--
          <div
            v-else-if="isPDFType(item.attachment_mime_type)"
            class="table-preview"
          >
            <v-icon x-large>
              {{ icons.mdiFilePdfBox }}
            </v-icon>
          </div>
          -->
          <div
            v-else
            class="table-preview"
          >
            {{ mimeType(item.attachment_mime_type) }}
          </div>
        </template>

        <!-- created date/time -->
        <template #item.created="{ item }">
          {{ formatDateTime(item.created) }}
        </template>

        <!-- size -->
        <template #item.size="{ item }">
          {{ Math.round(item.data.length / 1024) }}KB
        </template>

        <!-- delete -->
        <template #item.updated="{ item }">
          <v-icon
            v-if="item.updated"
            right
            color="error"
            @click.stop="deleteAttachment(null, item)"
          >
            {{ icons.mdiTrashCan }}
          </v-icon>
          <v-icon
            v-else
            right
            color="success"
            @click.stop="viewAttachment(item)"
          >
            {{ icons.mdiCloudCheckVariant }}
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-overlay
      v-if="showOverlay"
      :value="showOverlay"
    >
      <v-card
        class="attachment-overlay"
      >
        <v-img
          v-if="isImage"
          :src="localAttachments[imageViewIndex].data"
          contain
          @error="handleURLExpireError()"
        >
          <template #placeholder>
            <v-row
              class="fill-height ma-0"
              align="center"
              justify="center"
              style="flex-direction: column"
            >
              <v-progress-circular
                indeterminate
                size="64"
                width="7"
                color="primary"
                class="load-sync-progress"
              ></v-progress-circular>
              <p
                class="loading-message"
                style="color: var(--v-accent-lighten3)"
              >
                Loading...
              </p>
            </v-row>
          </template>
        </v-img>
        <v-row
          v-if="isPDF && loadingPDF"
          class="fill-height ma-0"
          align="center"
          justify="center"
          style="flex-direction: column"
        >
          <v-progress-circular
            indeterminate
            size="64"
            width="7"
            color="primary"
            class="load-sync-progress"
          ></v-progress-circular>
          <p
            class="loading-message"
            style="color: var(--v-accent-lighten3)"
          >
            Loading...
          </p>
        </v-row>
        <div v-if="isPDF">
          <pdf
            v-for="i in pagesPDF"
            :key="i"
            :src="srcPDF"
            :page="i"
            class="pdf-viewer"
            @loaded="pageLoaded(i)"
            @error="handleURLExpireError()"
          ></pdf>
        </div>
        <v-row
          align="center"
          justify="space-between"
          class="controls"
        >
          <btn
            label="Close"
            color="secondary"
            :icon="icons.mdiClose"
            @click="closeOverlay"
          ></btn>
          <div class="attachment-title">
            {{ imageTitle }}
          </div>
          <div>
            {{ formatDateTime(imageDate) }}
          </div>
          <btn
            v-if="localAttachments[imageViewIndex].updated"
            label="Delete"
            :icon="icons.mdiTrashCan"
            color="error"
            @click="deleteAttachment(imageViewIndex)"
          ></btn>
          <div
            v-if="localAttachments[imageViewIndex].updated !== true"
            class="synced"
          >
            <v-icon
              color="success"
              class="mr-1"
            >
              {{ icons.mdiCloudCheckVariant }}
            </v-icon>
            Synced
          </div>
        </v-row>
      </v-card>
    </v-overlay>
  </v-dialog>
</template>

<script>
/* eslint-disable no-param-reassign */

import {
  mdiClose, mdiFileImagePlus, mdiTrashCan, mdiCloudCheckVariant, mdiFilePdfBox, mdiDownload,
} from '@mdi/js'
import { mapGetters } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import pdf from '@teckel/vue-pdf'
import Vue from 'vue'
import { isHeic, heicTo } from 'heic-to'
import { forceCheckStorageUsageAfterAttachmentSave } from '@/utils/storageMonitor'

export default {
  components: { pdf },
  props: {
    attachmentModal: {
      type: Boolean,
      default: false,
    },
    attachmentTypeId: {
      type: [Number, String],
      default: null,
    },
    attachmentType: {
      type: String,
      default: null,
    },
    signed: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: null,
    },
    woundId: {
      type: String,
      default: null,
    },
    encounterAttachments: {
      type: Array,
      default: null,
    },
    fromPatientView: {
      type: Boolean,
      default: false,
    },
    imagesOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      srcPDF: null,
      pagesPDF: 1,
      loadingPDF: true,
      formValid: false,
      modalState: false,
      showOverlay: false,
      isImage: false,
      isPDF: false,
      imageViewIndex: null,
      imageTitle: null,
      imageDate: null,
      dialogAttachmentCreate: false,
      dialogAttachmentTypeId: null,
      dialogAttachmentType: null,
      encounterId: this.$route.query.id,
      isFileSelected: false,
      isAddingFile: false,
      selectedFile: null,
      file: [],
      attachment: {
        attachment_filename: null,
        attachment_mime_type: null,
        attachment_filepath: '',
        attachment_width: 0,
        attachment_height: 0,
        data: null,
      },
      localAttachments: [],
      fileDescription: null,
      imagePreview: null,
      icons: {
        mdiClose, mdiFileImagePlus, mdiTrashCan, mdiCloudCheckVariant, mdiFilePdfBox, mdiDownload,
      },
      headers: [
        {
          text: 'Image',
          value: 'data',
          align: 'center',
          sortable: false,
          width: 250,
          filter: value => !this.data || value === this.data,
        },
        {
          text: 'Description',
          value: 'attachment_filename',
          align: 'center',
          width: 200,
        },
        {
          text: 'Created',
          value: 'created',
          align: 'center',
        },
        {
          text: 'Size',
          value: 'size',
          sortable: false,
        },
        {
          text: 'Synced',
          value: 'updated',
          align: 'small',
        },
        {
          text: 'Bucket',
          value: 'attachment_type',
          align: 'hide',
          filter: value => !this.dialogAttachmentType || value === this.dialogAttachmentType,
        },
      ],
    }
  },
  computed: {
    ...mapGetters('baseData', ['bucketTypes']),
    ...mapGetters(['isOnline']),
    filteredAudioBucketType() {
      if (this.fromPatientView) {
        return this.bucketTypes.filter(bucket => bucket.value !== 'images' && bucket.value !== 'visit-dictation')
      }

      return this.bucketTypes.filter(bucket => bucket.value !== 'visit-dictation')
    },
    imageOnlyAttachments() {
      const filterByWound = this.woundId && this.dialogAttachmentType === 'images'

      return this.localAttachments.filter(item => !filterByWound || item.wound_id === this.woundId)
    },
  },
  watch: {
    attachmentModal() {
      this.modalState = this.attachmentModal
      this.dialogAttachmentTypeId = Number(this.attachmentTypeId)
      this.$emit('update:attachmentTypeId', null)
    },
    attachmentType() {
      this.dialogAttachmentType = this.attachmentType
    },
    showOverlay() {
      this.loadingPDF = true
      if (this.showOverlay && this.isPDF) {
        this.srcPDF = pdf.createLoadingTask(this.localAttachments[this.imageViewIndex]?.data)
        this.srcPDF.promise.then(file => {
          this.pagesPDF = file.numPages
        }).catch(e => console.error(e))
      }
    },
    encounterAttachments: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.localAttachments = JSON.parse(JSON.stringify(newVal))
            .map(item => ({
              ...item,
              data: item.data || 'placeholder',
            }))
        }
      },
    },
  },
  mounted() {
  },
  methods: {
    normalizeFilename() {
      // Removes non-ASCII characters
      this.fileDescription = this.fileDescription
        .replace(/[^\x00-\x7F]/g, '')        // remove non-ASCII
        .replace(/\//g, '-')                // replace / with -
        .replace(/[\\:*?"<>|]/g, '')       // remove \ : * ? " < > |
        .substring(0, 120)                // limit to 120 characters
        .trim()
    },
    pageLoaded(page) {
      if (page === this.pagesPDF) {
        setTimeout(() => { // Gives it a second to render, reduces flashing
          const pages = document.querySelectorAll('.pdf-viewer')
          pages.forEach(el => {
            el.style.height = 'auto'
            el.style.opacity = '1'
          })
          this.loadingPDF = false
        }, 1000)
      }
    },
    formatDate(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY')
    },
    formatDateTime(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY h:mm:ss A')
    },
    attachmentCount(bucket) {
      if (!Array.isArray(this.encounterAttachments)) return false

      const isWoundImages = this.woundId && bucket === 'images'

      return this.encounterAttachments.reduce((count, item) => {
        const matchesBucket = item.attachment_type === bucket
        const matchesWound = !isWoundImages || item.wound_id === this.woundId
        return count + (matchesBucket && matchesWound)
      }, 0)
    },
    isPDFType(mime) {
      return mime === 'application/pdf'
    },
    isImageType(mime) {
      return mime.substring(0, 5) === 'image'
    },
    mimeType(mime) {
      return mime.substring(mime.indexOf('/') + 1).toUpperCase()
    },
    viewAttachment(item) {
      if (item.data === 'placeholder') {
        if (this.isOnline) {
          try {
            this.downloadAttachment(item)
          } catch (e) {
            this.$store.dispatch('notify', { value: `Issue with retrieving attachment URL parameters. ${e}`, color: 'error' })
          }
        } else {
          this.$store.dispatch('notify', { value: 'Please connect to the internet to view attachment.', color: 'error' })
        }
      } else if (this.isImageType(item.attachment_mime_type) || this.isPDFType(item.attachment_mime_type)) {
        this.imageViewIndex = this.localAttachments.findIndex(x => x.id === item.id)
        this.imageTitle = item.attachment_filename
        this.imageDate = item.created
        // eslint-disable-next-line no-unused-expressions
        this.isImageType(item.attachment_mime_type) ? this.isImage = true : this.isPDF = true
        this.showOverlay = true
      } else {
        this.$store.dispatch('notify', { value: `Attachment must be an image or PDF to view, file type is: ${this.mimeType(item.attachment_mime_type)}` })
        this.closeOverlay()
      }
    },
    closeOverlay() {
      this.showOverlay = false
      this.imageViewIndex = null
      this.imageTitle = null
      this.imageDate = null
      this.isImage = false
      this.isPDF = false
    },
    deleteAttachment(index, item = null) {
      this.$root.confirm({
        title: 'Delete Attachment?',
        body: 'Are you sure you wish to delete this attachment?',
        cancel: 'No',
        confirm: 'Delete',
        confirmIcon: this.icons.mdiTrashCan,
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          this.closeOverlay()
          // eslint-disable-next-line no-param-reassign
          if (index === null) index = this.encounterAttachments.findIndex(x => x.id === item.id)
          if (index !== -1) {
            this.encounterAttachments.splice(index, 1)
          }
          this.updateAttachments()
        }
      })
    },
    attachmentTypeClicked(bucket) {
      this.dialogAttachmentType = bucket.value
    },
    selectAttachmentClicked() {
      this.$refs.uploader.click()
    },
    async testHEIC(file) {
      // eslint-disable-next-line no-return-await
      return await isHeic(file)
    },
    async convertHEIC(file) {
      return heicTo({
        blob: file,
        type: 'image/jpeg',
        quality: 0.5,
      })
    },
    async onFileChanged(e) {
      // validate selected file
      if (e.target.files.length > 0) {
        const selectedFile = e.target.files[0]
        const allowPDF = this.$store.getters['baseData/bucketTypeFromValue'](this.dialogAttachmentType).pdf
        let processedFile = selectedFile

        const isHEICFile = await isHeic(selectedFile)
        if (isHEICFile) {
          try {
            const convertedBlob = await this.convertHEIC(selectedFile)
            processedFile = new File([convertedBlob], selectedFile.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' })
          } catch (error) {
            this.$refs.uploader.value = null
            this.resetUploadValues()
            await this.$store.dispatch('notify', {
              value: 'Error converting HEIC/HEIF file. Please try a different image format.',
              color: 'error',
            })

            return
          }
        }

        if (this.isImageType(processedFile?.type) || (allowPDF && this.isPDFType(processedFile?.type))) {
          this.file = processedFile
          this.fileDescription = this.file.name.split('.').slice(0, -1).join('.')
          this.normalizeFilename()
          if (this.fileDescription === 'image') {
            this.fileDescription = `${this.$custom.toProperCase(this.dialogAttachmentType.slice(0, -1))} ${this.formatDate(Date.now())}`
          }
          this.imagePreview = URL.createObjectURL(this.file)
          this.attachment = {
            ...this.attachment,
            encounter_id: this.fromPatientView ? null : this.encounterId,
            patient_id: this.patientId,
            wound_id: this.dialogAttachmentType === 'images' ? this.woundId : null,
            attachment_type: this.dialogAttachmentType,
            attachment_mime_type: this.file.type,
            attachment_filename: this.fileDescription,
            attachment_filesize: this.file.size,
            data: this.imagePreview,
          }
          this.isFileSelected = true
        } else {
          this.$refs.uploader.value = null
          this.resetUploadValues()
          await this.$store.dispatch('notify', {
            value: `Attachment must be an image${allowPDF ? ' or PDF' : ''}, file type is: ${this.mimeType(selectedFile.type)}`,
            color: 'error',
          })
        }
      }
    },
    addFile() {
      this.normalizeFilename()
      if (!this.fileDescription) return

      this.isAddingFile = true

      const params = {
        attachment: {
          ...this.attachment,
          id: uuidv4(), // generate unique ID for the attachment
          updated: true,
          created: this.$custom.utcNow(), // current UTC timestamp
          attachment_filename: this.fileDescription
        },
        file: this.file
      }

      const reader = new FileReader()
      reader.readAsDataURL(params.file) // read file as DataURL for image processing or direct upload

      reader.onload = () => {
        // Check if the file is an image type
        if (this.isImageType(params.attachment.attachment_mime_type)) {
          const img = document.createElement('img')
          img.src = reader.result // load image data

          img.onload = () => {
            // Determine if file is small enough to skip resizing
            const isSmallFile = params.file.size <= 384000 // 384 KB threshold
            // Set JPEG quality: 85% for small files, 70% for large files to reduce size
            const quality = isSmallFile ? 0.85 : 0.7

            // Default dimensions to original image size
            let newWidth = img.width
            let newHeight = img.height

            // Resize only if file is large
            if (!isSmallFile) {
              const maxDimension = 1200
              const ratio = img.width / img.height
              // Scale dimensions proportionally, constraining the largest side to maxDimension
              if (img.width > img.height) {
                newWidth = maxDimension
                newHeight = maxDimension / ratio
              } else {
                newHeight = maxDimension
                newWidth = maxDimension * ratio
              }
            }

            // Create canvas for resizing and JPEG conversion
            const canvas = document.createElement('canvas')
            canvas.width = newWidth
            canvas.height = newHeight

            const ctx = canvas.getContext('2d')
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'

            // Draw the image to canvas with calculated dimensions
            ctx.drawImage(img, 0, 0, newWidth, newHeight)

            // Convert canvas image to JPEG data URL with specified quality
            const jpegData = canvas.toDataURL('image/jpeg', quality)

            // Update attachment details with new image info
            params.attachment.attachment_width = newWidth
            params.attachment.attachment_height = newHeight
            params.attachment.attachment_mime_type = 'image/jpeg'
            params.attachment.attachment_filesize = jpegData.length
            params.attachment.data = jpegData

            // Add the processed attachment and update UI/store
            this.encounterAttachments.push(params.attachment)
            this.updateAttachments()
            this.$store.dispatch('notify', {
              value: 'Attachment added successfully.',
              color: 'success'
            })
            this.resetUploadValues()
          }
        } else {
          // For non-image files, just use the original DataURL without processing
          params.attachment.data = reader.result
          this.encounterAttachments.push(params.attachment)
          this.updateAttachments()
          this.$store.dispatch('notify', {
            value: 'Attachment added successfully.',
            color: 'success'
          })
          this.resetUploadValues()
        }
      }
    },
    updateAttachments() {
      this.$store.commit('patients/updateAttachments', {
        id: this.patientId,
        attachments: this.encounterAttachments,
      })
      forceCheckStorageUsageAfterAttachmentSave(this.$store)
    },
    downloadAttachment(item) {
      const attachmentUrl = this.validateCreateUrl(item)

      this.$store.dispatch('encounters/syncingModal', !item.renewUrl)
      this.$store.dispatch('patients/getPatientAttachment', { urlString: attachmentUrl })
        .then(response => {
          if (response.status === 'success') {
            const imageData = `data:${response.data.contentType};base64,${response.data.data}`
            const attachmentIdx = this.localAttachments.findIndex(attachment => attachment.id === item.id)
            if (attachmentIdx !== -1) {
              const updatedAttachment = {
                ...this.localAttachments[attachmentIdx],
                data: imageData,
              }
              this.localAttachments.splice(attachmentIdx, 1, updatedAttachment)
            }

            this.$store.dispatch('encounters/syncingModal', false)
            if (!item.renewUrl) {
              this.$store.dispatch('notify', { value: 'Attachment finished downloading!', color: 'success' })
            }
          } else {
            this.$store.dispatch('encounters/syncingModal', false)
            this.$store.dispatch('notify', { value: 'Unable to download attachment', color: 'error' })
          }
        })
        .catch(e => Vue.prototype.$custom.processCommError(e, 'downloadAttachment'))
    },
    cancelUpload(outsideModalClick) {
      this.$root.confirm({
        title: 'Cancel Adding Attachment?',
        body: 'Are you sure you wish to cancel adding the attachment?',
        cancel: 'No',
        confirm: 'Yes',
      }).then(result => {
        if (result) {
          if (outsideModalClick) {
            this.closeModal()
          } else {
            this.resetUploadValues()
          }
        }
      })
    },
    handleURLExpireError() {
      const expiredAttachment = this.localAttachments[this.imageViewIndex]
      expiredAttachment.data = 'placeholder'
      expiredAttachment.renewUrl = true
      this.viewAttachment(expiredAttachment)
    },
    resetUploadValues() {
      this.file = []
      this.fileDescription = null
      this.isFileSelected = false
      this.isAddingFile = false
      this.attachment = {
        attachment_filename: null,
        attachment_mime_type: null,
        attachment_filepath: '',
        attachment_width: 0,
        attachment_height: 0,
        data: null,
      }
    },
    closeModal() {
      this.modalState = false
      this.dialogAttachmentType = null
      this.$emit('update:attachmentModal', this.modalState)
      this.$emit('update:attachmentType', this.dialogAttachmentType)
      this.resetUploadValues()
    },
    mimeTypeToExtension(mimeType) {
      const mimeTypeMap = {
        'application/pdf': '.pdf',
        'image/png': '.png',
        'image/jpg': '.jpg',
        'image/jpeg': '.jpeg',
        'image/tiff': '.tiff',
        'image/tif': '.tif',
        'image/heic': '.heic',
        'image/bmp': '.bmp',
        'image/wmf': '.wmf',
      }

      return mimeTypeMap[mimeType] || null
    },
    validateCreateUrl(attachment) {
      const { id } = attachment
      const filename = attachment.attachment_filename
      const mimeType = this.mimeTypeToExtension(attachment.attachment_mime_type)

      if (!id) {
        throw new Error('Attachment ID is missing.')
      }

      if (!filename) {
        throw new Error('Attachment filename is missing.')
      }

      if (!mimeType) {
        throw new Error(`Unknown MIME type: ${mimeType}.`)
      }

      return `${id}/${filename}${mimeType}`
    },
    clickOutside() {
      /* This causes this model to work differently than then rest of the app, so it was removed for consistency reasons
      if (this.isFileSelected) {
        this.cancelUpload(true)
      } else {
        this.closeModal()
      }
      */
    },
  },
}
</script>

<style lang="scss" scoped>
.attachments-modal {
  .table-preview {
    width: 100%;
    height: 75px;
    line-height: 75px;
  }
  .pdf-preview {
    max-width: 250px;
    max-height: 150px;
  }
}
.attachment-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: map-get($shades, 'white');
  border-radius: 0;
  overflow: hidden auto;
  .v-image {
    width: 100%;
    height: 91vh;
  }
  .pdf-viewer {
    width: 100%;
    height: 100vh; // If we don't specify a height, the PDF viewer will throw errors for some reason
    opacity: 0;
    border-bottom: 10px solid black;
    display: flex !important;
    align-items: center;
    transition: opacity 0.3s ease;
    &:last-of-type {
      margin-bottom: 95px;
    }
  }
  .controls {
    background-color: map-get($shades, 'white');
    flex-wrap: nowrap;
    z-index: 50;
    position: fixed;
    bottom: 12px;
    left: 12px;
    right: 12px;
    padding: 30px;
    > div {
      color: var(--v-accent-lighten3);
      padding: 0 10px;
      max-width: 40%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      &:first-of-type {
        padding-right: 0;
      }
    }
    > .synced {
      font-size: 1rem;
      color: rgba(94, 86, 105, 0.68) !important;
    }
  }
}
::v-deep {
  .image-preview {
    height: 46px;
    line-height: 46px;
    width: auto;
    border: 1px solid #aaa;
    border-radius: 5px;
    font-size: 1rem;
    &.mime-type {
      padding: 0 5px;
    }
  }
  .v-btn.v-btn--disabled {
    &.v-btn--active {
      background-color: var(--v-secondary-lighten3) !important;
      .v-btn__content {
        color: rgba(0, 0, 0, 0.42);
      }
    }
  }
  .v-data-table.disable {
    position: relative;
    .v-data-table__wrapper:before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: map-get($shades, 'white');
      z-index: 10;
      opacity: 0.5;
    }
  }
}
</style>
